import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft,
    FaKey
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "./ManageAdmins.css";
import UserPermissionsPanel from "../components/UserPermissionsPanel";

const API_URL = process.env.REACT_APP_API_URL;


function ManageAdmins() {

    const emptyForm = {
        id: "",
        username: "",
        password: "",
        first_name: "",
        last_name: "",
        email: "",
        mobile_no: "",
        is_active: true
    };

    const [admins, setAdmins] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [permissionsFor, setPermissionsFor] = useState(null); // { id, username } | null

    useEffect(() => {
        loadAdmins();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            admins.filter(item =>
                item.username.toLowerCase().includes(value) ||
                (item.first_name || "").toLowerCase().includes(value) ||
                (item.last_name || "").toLowerCase().includes(value)
            )

        );

    }, [search, admins]);

    const loadAdmins = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/admins`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (data.status) {

                setAdmins(data.data || []);

            } else {

                setAdmins([]);

                toast.error(data.message || "Failed to load admins");

            }

        } catch (err) {

            console.error(err);

            setAdmins([]);

            toast.error("Unable to load admins. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...emptyForm,
            ...item,
            password: ""
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return;

        setShowForm(false);

        setForm(emptyForm);

    };

    const saveAdmin = async () => {

        if (!form.username || (!isEdit && !form.password)) {

            toast.warn("Username and password are required");
            return;

        }

        setIsSaving(true);

        try {

            const res = await fetch(`${API_URL}/portal/admins`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(form)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(

                    data.message || (isEdit ? "Admin updated successfully" : "Admin created successfully")

                );

                loadAdmins();

            } else {

                toast.error(data.message || "Failed to save admin");

            }

        } catch (err) {

            console.error("Failed to save admin:", err);

            toast.error("Something went wrong while saving the admin");

        } finally {

            setIsSaving(false);

        }

    };

    const deleteAdmin = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to remove this admin? Users they created will remain, but they will lose access."
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/admins/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("Admin removed successfully");

                loadAdmins();

            } else {

                toast.error(data.message || "Failed to remove admin");

            }

        } catch (err) {

            console.error("Failed to remove admin:", err);

            toast.error("Something went wrong while removing the admin");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <div>
                    <h2>Manage Admins</h2>
                   {/*  <p className="page-subtitle">
                        SuperAdmin only. Set a username and password here for each Admin -
                        they'll use it to log in and manage their own Users.
                    </p> */}
                </div>

                {

                    !showForm && !permissionsFor &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add Admin

                    </button>

                }

            </div>

            {

                permissionsFor ? (

                    <UserPermissionsPanel
                        userId={permissionsFor.id}
                        username={permissionsFor.username}
                        onClose={() => setPermissionsFor(null)}
                    />

                ) : showForm ? (

                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update Admin" : "Add Admin"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="username"
                                placeholder="Username (login ID)"
                                value={form.username}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="password"
                                type="password"
                                placeholder={isEdit ? "New password (leave blank to keep current)" : "Password"}
                                value={form.password}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="first_name"
                                placeholder="First Name"
                                value={form.first_name || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="last_name"
                                placeholder="Last Name"
                                value={form.last_name || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="email"
                                placeholder="Email (optional)"
                                value={form.email || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="mobile_no"
                                placeholder="Mobile Number (optional)"
                                value={form.mobile_no || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            {

                                isEdit &&

                                <label className="checkbox-field">
                                    <input
                                        type="checkbox"
                                        name="is_active"
                                        checked={!!form.is_active}
                                        onChange={handleChange}
                                        disabled={isSaving}
                                    />
                                    Active
                                </label>

                            }

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={saveAdmin}
                                disabled={isSaving}
                            >

                                {
                                    isSaving
                                        ? <><FaSpinner className="spin" /> Saving...</>
                                        : isEdit
                                            ? "Update Admin"
                                            : "Create Admin"
                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by username or name..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>
                                        <th>Username</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Mobile</th>
                                        <th>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>
                                                <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading admins...
                                                </td>
                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>
                                                    <td colSpan="6" style={{ textAlign: "center", padding: "40px" }}>
                                                        No admins created yet - click "Add Admin" to get started.
                                                    </td>
                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.username}</td>

                                                        <td>{[item.first_name, item.last_name].filter(Boolean).join(" ") || "-"}</td>

                                                        <td>{item.email || "-"}</td>

                                                        <td>{item.mobile_no || "-"}</td>

                                                        <td>
                                                            <span className={`status-pill ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                <button
                                                                    type="button"
                                                                    className="icon-btn perm-btn"
                                                                    title="Custom permissions for this admin"
                                                                    aria-label="Custom permissions for this admin"
                                                                    onClick={() => setPermissionsFor({ id: item.id, username: item.username })}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaKey />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit admin"
                                                                    aria-label="Edit admin"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Remove admin"
                                                                    aria-label="Remove admin"
                                                                    onClick={() => deleteAdmin(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    {
                                                                        deletingId === item.id
                                                                            ? <FaSpinner className="spin" />
                                                                            : <FaTrash />
                                                                    }
                                                                </button>

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default ManageAdmins;
